import { apiWithPrefix as api } from '../utils/api';

/**
 * Leave Service
 * Handles all leave-related API calls
 */
export class LeaveService {
  /**
   * Get leave requests
   */
  static async getLeaveRequests({ status = 'all', page = 1, ...filters } = {}) {
    try {
      const response = await api.get('/leave-requests', { params: { ...filters, status, page } });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch leave requests');
    }
  }

  /**
   * Get user's leave requests
   */
  static async getMyRequests() {
    try {
      const response = await api.get('/leave-requests/my-requests');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch my leave requests');
    }
  }

  /**
   * Create leave request
   */
  static async createLeaveRequest(leaveData) {
    try {
      const response = await api.post('/leave-requests', leaveData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create leave request');
    }
  }

  /**
   * Update leave request status
   */
  static async updateLeaveRequest(id, data) {
    try {
      const response = await api.put(`/leave-requests/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update leave request');
    }
  }

  /**
   * Get leave balance
   */
  static async getLeaveBalance() {
    try {
      const response = await api.get('/leave-balance');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch leave balance');
    }
  }

  static async getEmployeeLeaveBalanceDetails(employeeEmail) {
    try {
      const response = await api.get('/leave-balance/details', {
        params: { employeeEmail },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employee leave balance details');
    }
  }

  static async createLeaveBalanceAdjustment(adjustment) {
    try {
      const response = await api.post('/leave-balance/adjustments', adjustment);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to adjust leave balance');
    }
  }

  /**
   * Get total leave days used
   */
  static async getTotalLeaveDays() {
    try {
      const response = await api.get('/total-leave-days');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch total leave days');
    }
  }

  /**
   * Get leave request status (pending count)
   */
  static async getLeaveRequestStatus() {
    try {
      const response = await api.get('/leave-request-status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch leave request status');
    }
  }

  /**
   * Get leave types
   */
  static async getLeaveTypes() {
    try {
      const response = await api.get('/leave-types');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch leave types');
    }
  }

  /**
   * Create leave type (Admin/HR only)
   */
  static async createLeaveType(leaveTypeData) {
    try {
      const response = await api.post('/leave-types', leaveTypeData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create leave type');
    }
  }

  /**
   * Update leave type (Admin/HR only)
   */
  static async updateLeaveType(id, leaveTypeData) {
    try {
      const response = await api.put(`/leave-types/${id}`, leaveTypeData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update leave type');
    }
  }

  /**
   * Delete leave type (Admin/HR only)
   */
  static async deleteLeaveType(id) {
    try {
      const response = await api.delete(`/leave-types/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete leave type');
    }
  }

  /**
   * Get leave requests for manager approval
   */
  static async getLeaveRequestsByManager(page = 1) {
    try {
      const response = await api.get('/getleaves-by-manager', {
        params: { page }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch leave requests for manager');
    }
  }

  /**
   * Get cancel requests
   */
  static async getCancelRequests(page = 1) {
    try {
      const response = await api.get('/cancel-requests', { params: { page } });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch cancel requests');
    }
  }

  // Cancellation status is a lookup for every personal leave, not a separate list.
  static async getAllCancelRequests() {
    const first = await this.getCancelRequests(1);
    const requests = [...(first?.requests || [])];
    for (let page = 2; page <= (first?.totalPages || 1); page += 1) {
      const response = await this.getCancelRequests(page);
      requests.push(...(response?.requests || []));
    }
    return { ...first, requests };
  }

  /**
   * Create cancel request
   */
  static async createCancelRequest(cancelData) {
    try {
      const response = await api.post('/cancel-requests', cancelData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create cancel request');
    }
  }

  /**
   * Get canceled requests by manager
   */
  static async getCanceledRequestsByManager(page = 1) {
    try {
      const response = await api.get('/getcanceled-requests-by-manager', { params: { page } });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch canceled requests');
    }
  }

  /**
   * Update cancel request
   */
  static async updateCancelRequest(id, data) {
    try {
      const response = await api.put(`/cancel-requests/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update cancel request');
    }
  }

  /**
   * Calculate leave days between dates
   */
  static calculateLeaveDays(fromDate, toDate, isFullDay = true) {
    const start = new Date(fromDate);
    const end = new Date(toDate);

    if (start > end) return 0;

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    return isFullDay ? diffDays : 0.5;
  }

  /**
   * Check if leave dates overlap with existing leaves
   */
  static async checkLeaveOverlap(fromDate, toDate) {
    try {
      const response = await api.post('/leave-requests/check-overlap', {
        fromDate,
        toDate
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check leave overlap');
    }
  }

  /**
   * Get leave calendar data
   */
  static async getLeaveCalendar(year, month) {
    try {
      const response = await api.get('/leave-requests/calendar', {
        params: { year, month }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch leave calendar');
    }
  }

  /**
   * Export leave report
   */
  static async exportLeaveReport(startDate, endDate, format = 'excel') {
    try {
      const response = await api.get('/leave-requests/export', {
        params: { startDate, endDate, format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export leave report');
    }
  }
}

export default LeaveService;
